import React, { useContext, useEffect, useMemo, useState } from 'react'
import { getPrice, translate } from '../utils/translator'
import { useParams } from 'react-router-dom'
import { useCartContext } from '../customHooks/useCart'
import { ShippingDataContext } from '../pages/Shipping/Shipping'
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider'
import { ImSpinner3 } from "react-icons/im";
import { MainTracker } from "../PixelTrackers/MainTracker";

import DisclaimerTooltip from './DisclaimerTooltip';
import { TOOLTIP_MEDICATION, TOOLTIP_SERVICE_FEE, TOOLTIP_TREATMENT_FEE, TOOLTIP_DOCTOR, ORDER_FLOWS, SERVICE_OPTIONS, TOOLTIP_SHIPPING_FEE } from '../utils/consts';
import { CANNABIS_SERVICE_FEE_TEST, SERVICE_FEE_TEST_VARIANT } from '../utils/consts';
import { displayedMedicationPrice, isPriceComponentIncluded, isZeroOrderPrice } from '../utils/helpers';
import ServiceFeeTestHelper from './ServiceFeeTestHelper';
import { carePlusPlanRowLabel } from '../pages/CarePlusShared/carePlusPlanLabel';

type Props = {
  setRxProductFinalPrice?: (price: number) => void
  shippingFee?: number
  isExpressShipping?: boolean
  trackFinalCartPrice?: boolean
  isMobileModal?: boolean
  setTotalPrice?: (price: number) => void
  isCannabisCatalog?: boolean
  onTooltipOpen?: (tooltipId: string) => void
  showDelayedCaptureDisclaimer?: boolean
  idPrefix?: string
  orderShippingType?: string
}

const CANNABIS_ID_TITLE = "Digitaler Cannabis-Ausweis";
const FREE_TOTAL_BADGE_BACKGROUND = "#11DDAC";
const FREE_TOTAL_PRICE_COLOR = "#0A9281";
const CartViewSummaryDE: React.FC<Props> = ({ setRxProductFinalPrice, setTotalPrice, shippingFee, trackFinalCartPrice = false, isExpressShipping = false, isCannabisCatalog = false, onTooltipOpen, showDelayedCaptureDisclaimer = false, idPrefix, orderShippingType }) => {

  const { language } = useParams()
  const { getCart, cartCurrency } = useCartContext();
  const cart = getCart();
  const isCarePlusOrder = Number(cart.care_plus_plan_fee ?? 0) > 0
  // Care+ shipping/payment stays on today's official-only display; setting is only for regular RX.
  const displayedPriceComponents = isCarePlusOrder ? null : (cart.displayed_price_components ?? null);
  const getId = (suffix: string) => idPrefix ? `${idPrefix}-${suffix}` : suffix;
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const { cannabisIDState } = useContext(ShippingDataContext) || {}; // Shipping page
  const { expressDeliveryState } = useShippingPageContext();
  const expressDeliveryPrice = expressDeliveryState?.deliveryPrice || 0;
  const deliveryFeeIsLoading = expressDeliveryState?.deliveryPriceLoading;
  const cannbisIdAsOrderProduct = cart.products.find(product => (product.type === "cannabis_id" || product.title === CANNABIS_ID_TITLE));
  const videoConsultationAsOrderProduct = cart.products.find(product => product.type === "online_vd"); // There is no other way to get this without breaking some types

  const isSchengenFlow = cart.flow === ORDER_FLOWS.SCHENGEN;
  const isSelfPickupSelected = cart.selected_service_option === SERVICE_OPTIONS.SELF_PICKUP;

  //TODO: switch with the shipping fee from props
  const actualShippingFee = useMemo(() => {
    if (isExpressShipping) {
      if (expressDeliveryPrice > 0) {
        return expressDeliveryPrice;
      }
      // For express shipping, prefer custom_shipping_fee (uber quote), then shipping_fee from getCartView
      if (cart.custom_shipping_fee && cart.custom_shipping_fee > 0) {
        return cart.custom_shipping_fee;
      }
    }
	if (isSelfPickupSelected) {
		return 0;
	}
	// Rx-only carts have no shipping of their own, but a mixed Rx+OTC cart still ships the OTC
	// items: keep the OTC threshold fee getCartView returns (the same one OrderManagerV2 charges).
	// Gated on otc_price so a cart that switches to Rx-only can never surface a leftover fee —
	// and read from shipping_fee (recomputed per cart view), never the stored custom_shipping_fee.
	if (cart.only_prescription) {
		return cart.otc_price ? cart.shipping_fee || 0 : 0;
	}
    // OTC-only carts store their per-cart shipping fee in custom_shipping_fee; shipping_fee is always 0 for this flow
    if (cart.flow === ORDER_FLOWS.OTC_ONLY) {
      // Prefer the cart field — saveCart refreshes it on every cart mutation. The shippingFee prop
      // is captured once on shipping-page load, so it goes stale when quantities change (e.g. the
      // out-of-stock modal dropping 2 → 1 must move the fee from the 0,20 € gap back to 4,99 €).
      return cart.custom_shipping_fee ?? shippingFee ?? 0;
    }
    return cart.shipping_fee || shippingFee || 0;
  }, [isExpressShipping, expressDeliveryPrice, shippingFee, cart.shipping_fee, cart.custom_shipping_fee]);


  const isCannabisOrder = useMemo(() => {
    if (isCannabisCatalog && !cart.only_prescription) {
      return true
    } else {
      return false
    }
  }, [cart.products])

  const summaryRows = useMemo(() => {

    const sumServiceFee = cart.products.filter(product => product.is_prescription_drug).reduce((sum, cartProduct) => sum += (cartProduct.service_fee || 0) * cartProduct.quantity, 0)
    const sumOfficialPrice = cart.products.filter(product => product.is_prescription_drug).reduce((sum, cartProduct) => sum += (cartProduct.official_price || 0) * cartProduct.quantity, 0)
    // Care+ (HL-9781): the plan fee already covers the service fee, so the medication line must
    // show the pure official price — otherwise the service fee is double-counted (once here, once
    // in the Care+ plan row) and the summed rows exceed the real charged total.
    let totalDrugsPrice = sumOfficialPrice + sumServiceFee
    if (isCannabisCatalog || isCarePlusOrder) {
      // Plan/cannabis already treat service separately — never bake SKU service into this line.
      totalDrugsPrice = sumOfficialPrice;
    } else if (displayedPriceComponents) {
      // Medication line is only the opted-in parts; excluded fees get their own rows below.
      totalDrugsPrice = cart.products
        .filter(product => product.is_prescription_drug)
        .reduce((sum, cartProduct) => sum + displayedMedicationPrice(cartProduct, displayedPriceComponents) * cartProduct.quantity, 0)
    }

    const totalOTCPrice = cart.products.filter(product => product.type === "free_drug" || product.type === "test_kit").reduce((sum, cartProduct) => {
		const itemPrice = isCannabisCatalog ? cartProduct.official_price : cartProduct.item_price;
		return sum += itemPrice * cartProduct.quantity;
	
	
	}, 0);


	const shouldShowServiceFee = (displayedPriceComponents ? !isPriceComponentIncluded(displayedPriceComponents, 'service_fee') && (cart.service_fee ?? 0) > 0 : (isCannabisCatalog || isSchengenFlow ? (cart.service_fee ?? 0) > 0 : false)) && Number(cart.care_plus_plan_fee ?? 0) === 0
    return {

      treatment_fee: {
        // Hide treatment if the setting already folded it into the medication number.
        shouldShow: Number(cart.treatment_fee ?? 0) > 0 && Number(cart.care_plus_plan_fee ?? 0) === 0 && (!displayedPriceComponents || !isPriceComponentIncluded(displayedPriceComponents, 'treatment_fee')),
        value: Number(cart.treatment_fee ?? 0),
      },

      // Care+ platform fee (HL-9781) — plan_fee × period_months, prepaid for the period.
      // Mirrors the amount OrderManagerV2 adds to the charged total; 0 for One-Time/Rx-Only/legacy.
      care_plus_plan_fee: {
        shouldShow: Number(cart.care_plus_plan_fee ?? 0) > 0,
        value: Number(cart.care_plus_plan_fee ?? 0),
      },

      service_fee: {
        shouldShow: shouldShowServiceFee,
        value: cart.service_fee || 0,
      },

      total_drugs: {
        shouldShow: Boolean(!cart.only_prescription) && totalDrugsPrice > 0,
        value: cart.only_prescription ? 0 : totalDrugsPrice
      },

      total_otc_drugs: {
        shouldShow: totalOTCPrice > 0,
        value: totalOTCPrice
      },

      cannabis_id: {
        shouldShow: Boolean(cannbisIdAsOrderProduct) || Boolean(cannabisIDState?.isCannabisIDAddedToOrder),
        value: cart.only_prescription ? (cannbisIdAsOrderProduct?.item_price ? cannbisIdAsOrderProduct.item_price : 0) : 0
      },

      shipping_fee: {
        shouldShow: videoConsultationAsOrderProduct || (Boolean(cart.only_prescription) && totalOTCPrice === 0) || (isCannabisCatalog && actualShippingFee === 0) || isPriceComponentIncluded(displayedPriceComponents, 'shipping_fee') ? false : true,
        value: 0,
      },

      consultation_fee: {
        shouldShow: cart.consultation_type === "video",
        value: cart.consultation_fee || 0,
      },

      coupon: {
        shouldShow: Boolean(cart.bonus_value) && !Boolean(cart.applied_subscription_discount) && Boolean(cart.only_prescription ? !cart.auto_bonus : true), // Auto bonus should not be applied on only prescription
        value: (-1) * Number(cart.bonus_value),
      },

      subscription_discount: {
        shouldShow: Boolean(cart.applied_subscription_discount),
        value: (-1) * Number(cart.applied_subscription_discount),
      }
    }

  }, [cart, 
	  cart.locale_id,
	  cart.products,
	  cannabisIDState?.isCannabisIDAddedToOrder,
	  cannabisIDState?.cost,
	  actualShippingFee]);

  const shippingFeeRowElement = useMemo(() => {
    let text;

	if(isCannabisCatalog && orderShippingType !== "express"){
		text = translate(language, "default", "OrderWindow/shipping_fee");
	}

	else if (cart.self_pickup_pharmacy_id ) {
		text = translate(language, "default", "OrderWindow/self_pickup");
	}

    // handle shipping text
    else if (cart.only_prescription && summaryRows.total_otc_drugs.value === 0) { //TODO: check if it's subsidized (mainly for FR catalogs) and if so, we need to show the shipping fee
      // text = translate(language, "default", "OrderWindow/prescription_delivery");
      // value = DISCOUNTED_SHIPPING_FEE
      return { text: "", price: "", isLoading: false }

    } else if (orderShippingType === "express") {
      text = translate(language, "default", "OrderWindow/express_delivery");
    } else {
      text = translate(language, "default", "OrderWindow/shipping_fee");
    }

    if (deliveryFeeIsLoading && isExpressShipping) {
      return { text: "", price: "", isLoading: true }
    }

let tooltipText: string | null = null;
        if(isCannabisCatalog){
            if (isExpressShipping) {
                tooltipText = translate(language, "default", "OrderWindow/express_delivery");
            } else {
                tooltipText = translate(language, "default", "OrderWindow/next_day_delivery");
            }
        }

        return { 
            text: text, 
            price: getPrice(actualShippingFee, cartCurrency.locale, cartCurrency.rate),
            isLoading: false,
            tooltipText: tooltipText
        }

  }, [cart, cartCurrency, isExpressShipping, actualShippingFee, deliveryFeeIsLoading])

  useEffect(() => {
    //TODO: This is backwards compatibility for old shipping, remove this once Shipping.js is deprecated
    if (setRxProductFinalPrice) {
      setRxProductFinalPrice(summaryRows.total_drugs.value + summaryRows.treatment_fee.value + summaryRows.subscription_discount.value + summaryRows.coupon.value);
    }
    if (setTotalPrice) {
      setTotalPrice(cartTotalPrice);
    }
  }, [summaryRows])



const doctorDisclaimerText = useMemo(() => {

  let text =""
  if(cart.doctor?.address) {
    text += translate(language, "default", "OrderWindow/adress") + ": " + cart.doctor?.address + "\n\n";
  }
  text += translate(language, "default", "OrderWindow/doctor_may_change_disclaimer");
  return text;
}, [cart.doctor?.address]);


  const cartTotalPrice = useMemo(() => {

    //Sum the total of summaryRows
    let sum = 0;

    Object.entries(summaryRows).forEach(([field, summaryRow]) => {
      if (summaryRow.shouldShow) {
        sum += summaryRow.value;
      }
    })
    sum += actualShippingFee;
    return sum

  }, [summaryRows, actualShippingFee, isCannabisOrder]);
  const isCartTotalFree = isZeroOrderPrice(cartTotalPrice);
  const originalTreatmentFee = summaryRows.treatment_fee.value;
  const shouldShowStruckOriginalTreatmentFee = isCartTotalFree && originalTreatmentFee > 0;
  const displayedTreatmentFee = isCartTotalFree ? 0 : originalTreatmentFee;
  useEffect(() => {
    const cartFinalPriceElement = document.getElementById(getId("cart_final_price"));
    if (cartFinalPriceElement) {
      if (sessionStorage.getItem('paymentEventSent') && trackFinalCartPrice) {
        MainTracker.track("products", "Final Cart Price", { "Cart Price": cartFinalPriceElement.textContent, "Customer ID": sessionStorage.getItem('paymentEventSent') });
        sessionStorage.removeItem('paymentEventSent');

      }
    }
  }, [cartTotalPrice]);


  	return (
			<ul id={getId("cart-view-summary")} className="flex flex-col gap-y-1">
				{/* Drugs Price - Medication */}
				{summaryRows['total_drugs'].shouldShow && (
					<li id={getId("cart-view-summary-row-total-drugs")} className="flex justify-between items-center text-sm text-[#656D79]">
						<div className="flex items-center gap-2">
							<p id={getId("cart-view-summary-row-text-total-drugs")} className="font-roboto">
								{isCannabisCatalog
									? translate(language, 'default', 'OrderWindow/drug_price_cannabis_catalog')
									: displayedPriceComponents && !isPriceComponentIncluded(displayedPriceComponents, 'service_fee')
											? translate(language, 'default', 'OrderWindow/drug_price')
											: translate(language, 'default', 'OrderWindow/drug_price_all_included')}
							</p>
							{!!isCannabisCatalog && (
								<DisclaimerTooltip
									isOpen={openTooltip === TOOLTIP_MEDICATION}
									onToggle={() => {
										const newState = openTooltip === TOOLTIP_MEDICATION ? null : TOOLTIP_MEDICATION;
										setOpenTooltip(newState);
										if (newState === TOOLTIP_MEDICATION) {
											onTooltipOpen?.(TOOLTIP_MEDICATION);
										}
									}}
									tooltipText={translate(language, 'default', 'OrderWindow/tooltip_medication')}
									tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
								/>
							)}
						</div>
						<span id={getId("cart-view-summary-row-value-total-drugs")} className="font-roboto" translate="no">
							{getPrice(summaryRows['total_drugs'].value, cartCurrency.locale, cartCurrency.rate)}
						</span>
					</li>
				)}

				{/* Treatment Fee  */}
				{summaryRows['treatment_fee'].shouldShow && (
					<li id={getId("cart-view-summary-row-treatment-fee")} className="flex justify-between items-center text-sm text-[#656D79]">
						<div className="flex items-center gap-2">
							<p id={getId("cart-view-summary-row-text-treatment-fee")} className="font-roboto">
								{translate(language, 'default', 'OrderWindow/treatment_fee')}
							</p>
							{!!isCannabisCatalog && (
								<DisclaimerTooltip
									isOpen={openTooltip === TOOLTIP_TREATMENT_FEE}
									onToggle={() => {
										const newState = openTooltip === TOOLTIP_TREATMENT_FEE ? null : TOOLTIP_TREATMENT_FEE;
										setOpenTooltip(newState);
										if (newState === TOOLTIP_TREATMENT_FEE) {
											onTooltipOpen?.(TOOLTIP_TREATMENT_FEE);
										}
									}}
									tooltipText={translate(language, 'default', 'OrderWindow/tooltip_treatment_fee')}
									tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
								/>
							)}
						</div>
						<div className="flex items-center gap-2">
							{shouldShowStruckOriginalTreatmentFee && (
								<span className="font-roboto line-through" translate="no">
									{getPrice(originalTreatmentFee, cartCurrency.locale, cartCurrency.rate)}
								</span>
							)}
							<span
								id={getId("cart-view-summary-row-value-treatment-fee")}
								className="font-roboto"
								style={isCartTotalFree ? { color: FREE_TOTAL_PRICE_COLOR } : undefined}
								translate="no"
							>
								{getPrice(displayedTreatmentFee, cartCurrency.locale, cartCurrency.rate)}
							</span>
						</div>
					</li>
				)}
				{/* Care+ Plan Fee (HL-9781) — label follows the selected tier (Care+ / Care+ Pro / Care+ Max). */}
				{summaryRows['care_plus_plan_fee'].shouldShow && (
					<li id={getId("cart-view-summary-row-care-plus-plan-fee")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p id={getId("cart-view-summary-row-text-care-plus-plan-fee")} className="font-roboto">
							{carePlusPlanRowLabel(cart.care_plus_plan_tier, language, translate)}
						</p>
						<span id={getId("cart-view-summary-row-value-care-plus-plan-fee")} className="font-roboto">
							{getPrice(summaryRows['care_plus_plan_fee'].value, cartCurrency.locale, cartCurrency.rate)}
						</span>
					</li>
				)}

				{/* Service Fee  */}
				{summaryRows['service_fee'].shouldShow && (
					<li id={getId("cart-view-summary-row-service-fee")} className="flex flex-col text-sm text-[#656D79]">
						<div className="flex justify-between items-center">
							<div className="flex items-center gap-2">
								<p id={getId("cart-view-summary-row-text-service-fee")} className="font-roboto">
									{translate(language, 'default', displayedPriceComponents ? 'OrderWindow/service_fee_rx_catalog' : 'OrderWindow/service_fee_cannabis_catalog')}
								</p>
								{!!isCannabisCatalog && (
									<DisclaimerTooltip
										isOpen={openTooltip === TOOLTIP_SERVICE_FEE}
										onToggle={() => {
											const newState = openTooltip === TOOLTIP_SERVICE_FEE ? null : TOOLTIP_SERVICE_FEE;
											setOpenTooltip(newState);
											if (newState === TOOLTIP_SERVICE_FEE) {
												onTooltipOpen?.(TOOLTIP_SERVICE_FEE);
											}
										}}
										tooltipText={cart.service_fee_test_variant === SERVICE_FEE_TEST_VARIANT.C && !cart.service_fee_test_non_flower_present
											? translate(language, 'default', 'OrderWindow/tooltip_service_fee_test')
												.replace(':flowerFee1', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[1], cartCurrency.locale, cartCurrency.rate))
												.replace(':flowerFee2', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[2], cartCurrency.locale, cartCurrency.rate))
												.replace(':flowerFee3', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT_C[3], cartCurrency.locale, cartCurrency.rate))
												.replace(':nonFlowerFee', getPrice(CANNABIS_SERVICE_FEE_TEST.NON_FLOWER_FEE, cartCurrency.locale, cartCurrency.rate))
											: cart.service_fee_test_variant === SERVICE_FEE_TEST_VARIANT.TEST
											? translate(language, 'default', 'OrderWindow/tooltip_service_fee_test')
												.replace(':flowerFee1', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[1], cartCurrency.locale, cartCurrency.rate))
												.replace(':flowerFee2', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[2], cartCurrency.locale, cartCurrency.rate))
												.replace(':flowerFee3', getPrice(CANNABIS_SERVICE_FEE_TEST.FLOWER_FEE_BY_COUNT[3], cartCurrency.locale, cartCurrency.rate))
												.replace(':nonFlowerFee', getPrice(CANNABIS_SERVICE_FEE_TEST.NON_FLOWER_FEE, cartCurrency.locale, cartCurrency.rate))
											: translate(language, 'default', 'OrderWindow/tooltip_service_fee')}
										tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
									/>
								)}
							</div>
							<span id={getId("cart-view-summary-row-value-service-fee")} className="font-roboto" translate="no">
								{getPrice(summaryRows['service_fee'].value, cartCurrency.locale, cartCurrency.rate)}
							</span>
						</div>
						<ServiceFeeTestHelper
							as="span"
							variant={cart.service_fee_test_variant}
							flowerCount={cart.service_fee_test_flower_count ?? 0}
							nonFlowerPresent={cart.service_fee_test_non_flower_present ?? false}
							language={language}
							id={getId("cart-view-summary-service-fee-test-helper")}
							className="font-roboto text-[12px] text-[#656D79] mt-1 whitespace-nowrap"
						/>
					</li>
				)}

				{/* OTC Drugs Price - Non-Medication */}
				{summaryRows['total_otc_drugs'].shouldShow && (
					<li id={getId("cart-view-summary-row-total-otc-drugs")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p id={getId("cart-view-summary-row-text-total-otc-drugs")} className="font-roboto">
							{translate(language, 'default', 'OrderSummary/otc_addons_price')}
						</p>
						<span id={getId("cart-view-summary-row-value-total-otc-drugs")} className="font-roboto" translate="no">
							{getPrice(summaryRows['total_otc_drugs'].value, cartCurrency.locale, cartCurrency.rate)}
						</span>
					</li>
				)}

				{/* Consultation Fee  */}
				{summaryRows['consultation_fee'].shouldShow && (
					<li id={getId("cart-view-summary-row-consultation-fee")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p className="font-roboto">{translate(language, 'default', cart.is_follow_up_consultation ? 'OrderWindow/consultation_fee_follow_up' : 'OrderWindow/consultation_fee')}:</p>
						<span id={getId("cart-view-summary-row-value-consultation_fee")} className="font-roboto" translate="no">
							{' '}
							{getPrice(summaryRows['consultation_fee'].value, cartCurrency.locale, cartCurrency.rate)}{' '}
						</span>
					</li>
				)}

				{/* Digital Cannabis ID Price row  */}
				{summaryRows['cannabis_id'].shouldShow && (
					<li id={getId("cart-view-summary-row-cannabis-id")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p className="font-roboto">{translate(language, 'default', 'OrderWindow/digital_cannabis_id')}</p>
						<span id={getId("cart-view-summary-row-value-cannabis_id")} className="font-roboto" translate="no">
							{' '}
							{getPrice(summaryRows['cannabis_id'].value, cartCurrency.locale, cartCurrency.rate)}{' '}
						</span>
					</li>
				)}

				{/* Shipping Fee - Delivery */}
				{summaryRows['shipping_fee'].shouldShow && shippingFeeRowElement && shippingFeeRowElement.text && (
					<li id={getId("cart-view-summary-row-shipping-fee")} className="flex justify-between items-center text-sm text-[#656D79]">
						<div className="flex items-center gap-2">
							<p id={getId("cart-view-summary-row-text-shipping-fee")} className="font-roboto">
								{shippingFeeRowElement.text}
							</p>
							
							{!!isExpressShipping && (
								<DisclaimerTooltip
									isOpen={openTooltip === TOOLTIP_SHIPPING_FEE}
									onToggle={() => {
										const newState = openTooltip === TOOLTIP_SHIPPING_FEE ? null : TOOLTIP_SHIPPING_FEE;
										setOpenTooltip(newState);
										if (newState === TOOLTIP_SHIPPING_FEE) {
											onTooltipOpen?.(TOOLTIP_SHIPPING_FEE);
										}
									}}
									tooltipText={(() => {
									const doctorApprovalFee = cart.fast_approval_fee ?? 0;
									const customShipping = cart.custom_shipping_fee ?? 0;
									const deliveryFee = expressDeliveryPrice > 0
										? Math.max(0, expressDeliveryPrice - doctorApprovalFee)
										: (doctorApprovalFee > 0 && customShipping >= 10
											? Math.max(0, customShipping - doctorApprovalFee)
											: customShipping);
									return translate(language, "default", "OrderWindow/express_delivery_price_tooltip")
									.replace(':deliveryFee', getPrice(deliveryFee, cartCurrency.locale, cartCurrency.rate))
									.replace(':doctorApprovalFee', getPrice(doctorApprovalFee, cartCurrency.locale, cartCurrency.rate));
								})()}
									tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
								/>
							)}
						</div>
						<span id={getId("cart-view-summary-row-value-shipping-fee")} className={`font-roboto${actualShippingFee > 0 ? '' : ' font-bold text-[#269C6E]'}`}>
							{shippingFeeRowElement.isLoading ? <ImSpinner3 className="animate-spin" /> : actualShippingFee > 0 ? shippingFeeRowElement.price : translate(language, 'default', 'OrderWindow/shipping_fee_free')}
						</span>
					</li>
				)}
				{/* Coupon discount */}
				{summaryRows['coupon'].shouldShow && (
					<li id={getId("cart-view-summary-row-coupon")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p className="font-roboto"> {translate(language, 'default', 'OrderWindow/coupon')} </p>
						<span className="font-roboto text-[#269C6E]" translate="no"> {getPrice(summaryRows['coupon'].value, cartCurrency.locale, cartCurrency.rate)} </span>
					</li>
				)}

				{/* Applied subscription discount */}
				{summaryRows['subscription_discount'].shouldShow && (
					<li id={getId("cart-view-summary-row-subscription-discount")} className="flex justify-between items-center text-sm text-[#656D79]">
						<p className="font-roboto"> {translate(language, 'default', 'OrderWindow/treatment_plan_discount')} </p>
						<span className="font-roboto text-[#269C6E]" translate="no">
							{' '}
							{getPrice(summaryRows['subscription_discount'].value, cartCurrency.locale, cartCurrency.rate)}{' '}
						</span>
					</li>
				)}

				{/* Fast Review Fee */}

			{/* Cart total Price */}
			<li id={getId("cart-view-summary-row-total-price")} className="flex justify-between items-center text-base text-[#0D2C54] mt-2">
				<p className="font-roboto font-semibold text-[#0D2C54] text-lg"> {translate(language, 'default', 'OrderWindow/total_price')} </p>
				<div className={isCartTotalFree ? "flex items-center gap-2" : "flex flex-col items-end"}>
					{isCartTotalFree && (
						<span
							className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
							style={{ backgroundColor: FREE_TOTAL_BADGE_BACKGROUND }}
						>
							{translate(language, 'default', 'OrderWindow/free_badge') || "FREE"}
						</span>
					)}
					<span
						id={getId("cart_final_price")}
						className={isCartTotalFree ? "font-roboto font-semibold" : "font-roboto font-semibold text-[#0D2C54]"}
						style={isCartTotalFree ? { color: FREE_TOTAL_PRICE_COLOR } : undefined}
						translate="no"
					>
						{getPrice(cartTotalPrice, cartCurrency.locale, cartCurrency.rate)}
					</span>
				</div>
			</li>
				{/* doctor name */}
				{cart.doctor && !isSchengenFlow ? (
					<li id={getId("cart-view-summary-row-doctor-name")} className="flex justify-between items-start text-sm text-[#656D79]">
						<p id={getId("cart-view-summary-row-text-doctor-name")} className="font-Poppins text-[#0D2C54] font-poppins font-medium text-[13px]">
							{translate(language, 'default', 'OrderWindow/doctor_name')}
						</p>

						<div className="flex flex-col gap-1 items-end">
							<div className="flex flex-row gap-1">
								<span id={getId("cart-view-summary-row-value-doctor-name")} className="font-roboto text-[13px]">
									{cart.doctor.fullName}
								</span>

								<DisclaimerTooltip
									isOpen={openTooltip === TOOLTIP_DOCTOR}
									onToggle={() => {
										const newState = openTooltip === TOOLTIP_DOCTOR ? null : TOOLTIP_DOCTOR;
										setOpenTooltip(newState);
										if (newState === TOOLTIP_DOCTOR) {
											onTooltipOpen?.(TOOLTIP_DOCTOR);
										}
									}}
									tooltipText={doctorDisclaimerText}
									tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
								/>
							</div>
						</div>
					</li>
				) : null}
			</ul>
		);
}

export default CartViewSummaryDE;
